/**
 * ToastProvider - Unified toast notification system
 * LAYER-SHARED-INTERACTIONS
 *
 * Uses sonner library for toast notifications.
 * Position: bottom-right (per DESIGN-DECISIONS.md)
 *
 * Usage:
 *   import { toast } from 'sonner';
 *   toast.success('Transaction completed');
 *   toast.error('Failed to save');
 *   toast.info('Document uploaded');
 *   toast.warning('Session expires in 5 minutes');
 */
import React from 'react';
import { Toaster, toast } from 'sonner';

/**
 * Toast duration configuration (from DESIGN-DECISIONS.md):
 * - Success: 3 seconds
 * - Error: 5 seconds (or until dismissed)
 * - Warning: 4 seconds
 * - Info: 3 seconds
 */
const TOAST_DURATION = {
  success: 5000,
  error: 5000,
  warning: 4000,
  info: 3000,
} as const;

export interface ToastProviderProps {
  children: React.ReactNode;
}

/**
 * ToastProvider wraps the application to enable toast notifications.
 * Add this at the root of your app.
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  return (
    <>
      {children}
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        duration={TOAST_DURATION.info}
        toastOptions={{
          classNames: {
            toast: 'esig-toast',
            title: 'esig-toast-title',
            description: 'esig-toast-description',
            success: 'esig-toast-success',
            error: 'esig-toast-error',
            warning: 'esig-toast-warning',
            info: 'esig-toast-info',
          },
        }}
      />
    </>
  );
};

/**
 * Pre-configured toast helpers with proper durations
 */
export const showToast = {
  success: (message: string, description?: string) =>
    toast.success(message, { description, duration: TOAST_DURATION.success }),

  error: (message: string, description?: string) =>
    toast.error(message, { description, duration: TOAST_DURATION.error }),

  warning: (message: string, description?: string) =>
    toast.warning(message, { description, duration: TOAST_DURATION.warning }),

  info: (message: string, description?: string) =>
    toast.info(message, { description, duration: TOAST_DURATION.info }),

  loading: (message: string) =>
    toast.loading(message),

  dismiss: (toastId?: string | number) =>
    toast.dismiss(toastId),

  promise: <T,>(
    promise: Promise<T>,
    messages: { loading: string; success: string; error: string }
  ) => toast.promise(promise, messages),
};

export { toast };
export default ToastProvider;
